<!--
  The upload area of the Bunny topic editor: a drop zone, a three-step
  progress indicator, and the controls each stage is allowed to show.

  Presentational only. It does not know Vuex exists and it does not know TUS
  exists -- it renders whatever uploadStage() returns and reports clicks
  upward. That boundary is what lets it be tested without mounting a store,
  and it leaves the TUS orchestration and polling in VdoSetTopicEditBunny.vue
  where they already work.
-->
<template>
  <div class="bunny-upload-panel">

    <!-- Stepper -->
    <div class="flex items-center mb-4" data-test="stepper">
      <template v-for="(step, index) in steps">
        <div :key="`dot-${step.n}`" class="flex flex-col items-center">
          <div class="bunny-step-dot" :class="`is-${stepState(step.n)}`">
            <feather-icon v-if="stepState(step.n) === 'done'"
                          icon="CheckIcon" svgClasses="h-4 w-4"/>
            <feather-icon v-else-if="stepState(step.n) === 'error'"
                          icon="AlertTriangleIcon" svgClasses="h-4 w-4"/>
            <span v-else>{{ step.n }}</span>
          </div>
          <span class="text-xs mt-1">{{ $t(step.label) }}</span>
        </div>
        <div v-if="index < steps.length - 1"
             :key="`bar-${step.n}`"
             class="bunny-step-bar"
             :class="{'is-done': stage.step > step.n}"></div>
      </template>
    </div>

    <!-- Headline for the current stage -->
    <div v-if="stage.stage !== STAGE.EMPTY" class="mb-2 flex items-center">
      <div v-if="stage.state === 'active'" class="bunny-spinner mr-2"></div>
      <feather-icon v-else-if="stage.state === 'error'"
                    icon="AlertTriangleIcon" class="text-danger mr-2"
                    svgClasses="h-5 w-5"/>
      <feather-icon v-else-if="stage.state === 'done'"
                    icon="CheckCircleIcon" class="text-success mr-2"
                    svgClasses="h-5 w-5"/>
      <span class="text-sm" data-test="headline">{{ $t(headlineKey) }}</span>
    </div>

    <!-- Progress, upload only -->
    <div v-if="isUploading" class="mb-4" data-test="progress">
      <vs-progress :percent="percent" color="primary"></vs-progress>
      <span class="text-sm">{{ percent }}%</span>
    </div>

    <!--
      Bunny's own status name, small. This is the only place raw Bunny
      vocabulary appears: it is what tells a stuck encode from a slow one
      without opening the Bunny dashboard.
    -->
    <div v-if="subStatus" class="text-xs text-grey mb-4" data-test="substatus">
      {{ subStatus }}
    </div>

    <!-- Drop zone. Unmounted while anything is in flight, which also resets
         the underlying Dropzone instance for the next selection.

         vue2-dropzone renders a plain, unfocusable div and hides its file
         input with width:0;height:0;visibility:hidden, so with none of the
         attributes below it could not be reached by keyboard at all -- a
         WCAG 2.1 AA 2.1.1 regression against the native <input type="file">
         this replaced. tabindex/role/aria-label make it a focusable button
         for assistive tech, and Enter/Space trigger the same click handler
         vue2-dropzone wires to a mouse click on its root element (which
         opens the hidden file input) -- see triggerDropzoneClick. -->
    <vue-dropzone v-if="stage.showDropzone"
                  id="bunny-dropzone"
                  ref="dropzone"
                  data-test="dropzone"
                  tabindex="0"
                  role="button"
                  :aria-label="$t('vdo-set-topic.bunny-stage.drop-here')"
                  :options="dropzoneOptions"
                  :useCustomSlot="true"
                  @vdropzone-file-added="onFileAdded"
                  @keydown.enter.native.prevent="triggerDropzoneClick"
                  @keydown.space.native.prevent="triggerDropzoneClick">
      <p class="text-lg text-success text-bold">{{ $t('vdo-set-topic.bunny-stage.drop-here') }}</p>
      <p class="text-sm">{{ $t('vdo-set-topic.bunny-stage.drop-hint') }}</p>
    </vue-dropzone>

    <!-- Controls -->
    <div class="vx-row mt-4">
      <div v-if="stage.canCancel" class="vx-col sm:w-1/4 w-full">
        <vs-button data-test="cancel" class="w-full" color="danger" type="border"
                   :disabled="disabled" @click="$emit('cancel')">
          {{ $t('general.cancel') }}
        </vs-button>
      </div>
      <div v-if="stage.canRefresh" class="vx-col sm:w-1/4 w-full">
        <vs-button data-test="refresh" class="w-full" type="border"
                   :disabled="disabled" @click="$emit('refresh')">
          {{ $t('general.refresh') }}
        </vs-button>
      </div>
      <div v-if="showDownload && stage.canDownload" class="vx-col sm:w-1/4 w-full">
        <vs-button data-test="download" class="w-full" color="primary"
                   :disabled="disabled" @click="$emit('download')">
          <feather-icon icon="DownloadIcon" svgClasses="h-4 w-4"/>
          {{ $t('general.download') }}
        </vs-button>
      </div>
      <div v-if="stage.canDelete" class="vx-col sm:w-1/4 w-full">
        <vs-button data-test="delete" class="w-full" color="danger"
                   :disabled="disabled" @click="$emit('delete')">
          <feather-icon icon="TrashIcon" svgClasses="h-4 w-4"/>
          {{ $t(deleteLabelKey) }}
        </vs-button>
      </div>
    </div>

  </div>
</template>

<script>
import VueDropzone from 'vue2-dropzone';
import 'vue2-dropzone/dist/vue2Dropzone.min.css';
import {uploadStage, STAGE} from '@/utils/bunny-upload-stage';
import {STATUS_NAMES} from '@/utils/bunny-status';

// Mirrors dropzoneOptions.acceptedFiles below. vue2-dropzone's own
// acceptedFiles filter runs AFTER it emits vdropzone-file-added, so the
// event fires for every dragged/dropped file regardless of extension -- this
// is the only enforcement point for the drag path.
const ACCEPTED_EXTENSIONS = ['.mov', '.mp4', '.m4a', '.m4v', '.3gp', '.3g2', '.mj2', '.wav'];

// How often the panel re-reads the clock. Both time-dependent readings below
// -- the "N min elapsed" line and the 24-hour isAbandoned window -- live
// inside computeds, and a computed that calls Date.now() never recomputes,
// because a clock is not a reactive dependency. Nothing else invalidates them
// either: Bunny sits in one status for minutes at a time and the 10-second
// poll writes back the same bunnyStatus and the same bunnyStatusAt each tick.
// Elapsed is rendered in whole minutes, so half a minute is the coarsest
// interval that cannot leave a stale minute on screen for long.
const CLOCK_TICK_MS = 30000;

// Stages where the video is still in Bunny's hands and a growing elapsed time
// is the signal. ABANDONED is included: it is defined by elapsed crossing 24h,
// so hiding the number there would remove the evidence for the state.
const TIMED_STAGES = [STAGE.PROCESSING, STAGE.ABANDONED];

const HEADLINE_KEYS = {
  [STAGE.EMPTY]: '',
  [STAGE.UPLOADING]: 'vdo-set-topic.bunny-stage.uploading',
  [STAGE.PROCESSING]: 'vdo-set-topic.bunny-stage.processing',
  [STAGE.READY]: 'vdo-set-topic.bunny-stage.ready',
  [STAGE.FAILED]: 'vdo-set-topic.bunny-stage.failed',
  [STAGE.ABANDONED]: 'vdo-set-topic.bunny-stage.abandoned',
};

export default {
  name: 'BunnyUploadPanel',
  components: {VueDropzone},
  props: {
    topic: {type: Object, default: null},
    isUploading: {type: Boolean, default: false},
    percent: {type: Number, default: 0},
    disabled: {type: Boolean, default: false},
    // Defaults to false so the download button cannot reappear by being
    // forgotten at a call site: the feature is off until something passes
    // the flag in. See isVideoDownloadEnabled in @/utils/video-provider.
    showDownload: {type: Boolean, default: false},
  },
  data() {
    return {
      STAGE,
      // The reactive clock the two time-dependent computeds read instead of
      // Date.now(). Ticked by the interval started in mounted().
      now: Date.now(),
      steps: [
        {n: 1, label: 'vdo-set-topic.bunny-stage.step-upload'},
        {n: 2, label: 'vdo-set-topic.bunny-stage.step-process'},
        {n: 3, label: 'vdo-set-topic.bunny-stage.step-ready'},
      ],
      // Dropzone requires a url even when it will never post: it throws on
      // construction without one. autoProcessQueue false is what actually
      // stops it -- the file goes to tus-js-client instead, via the
      // file-selected event.
      dropzoneOptions: {
        url: 'https://example.invalid/never-posted',
        autoProcessQueue: false,
        acceptedFiles: '.mov,.mp4,.m4a,.m4v,.3gp,.3g2,.mj2,.wav',
        maxFiles: 1,
        addRemoveLinks: false,
        previewsContainer: false,
      },
    };
  },
  computed: {
    stage() {
      return uploadStage({isUploading: this.isUploading, topic: this.topic, now: this.now});
    },
    headlineKey() {
      return HEADLINE_KEYS[this.stage.stage];
    },
    deleteLabelKey() {
      return this.stage.stage === STAGE.FAILED ?
        'vdo-set-topic.bunny-stage.delete-and-retry' :
        'vdo-set-topic.delete-only-video';
    },
    subStatus() {
      if (this.stage.stage === STAGE.EMPTY || this.isUploading) return '';

      const parts = [];
      const name = STATUS_NAMES[this.topic && this.topic.bunnyStatus];
      if (name) parts.push(name);

      const at = this.statusAtMs;
      if (at !== null) {
        const time = new Date(at);
        parts.push(this.$t('vdo-set-topic.bunny-stage.since', {
          time: `${pad(time.getHours())}:${pad(time.getMinutes())}`,
        }));
        // Elapsed answers "is this encode wedged?", so it only belongs on the
        // stages that are still waiting on Bunny. READY and FAILED are
        // terminal: bunnyStatusAt stops moving but the clock does not, so the
        // line would keep climbing forever on a video that finished -- reading
        // as though something were still running. `since` stays either way;
        // it is a fixed timestamp, not a counter.
        if (TIMED_STAGES.indexOf(this.stage.stage) !== -1) {
          parts.push(this.$t('vdo-set-topic.bunny-stage.elapsed', {
            minutes: Math.max(0, Math.floor((this.now - at) / 60000)),
          }));
        }
      }

      return parts.join(' · ');
    },
    // bunnyStatusAt arrives as an all-digits epoch-milliseconds string, not
    // ISO-8601 -- graphql-js serialises Prisma DateTime through
    // Date.prototype.valueOf(). Accept both, same as isAbandoned does.
    statusAtMs() {
      const raw = this.topic && this.topic.bunnyStatusAt;
      if (!raw) return null;
      const ms = /^\d+$/.test(raw) ? Number(raw) : new Date(raw).getTime();
      return isNaN(ms) ? null : ms;
    },
  },
  methods: {
    stepState(n) {
      if (n < this.stage.step) return 'done';
      if (n === this.stage.step) return this.stage.state;
      return 'upcoming';
    },
    onFileAdded(file) {
      // Cheap first guard, not the thing that actually stops a fast
      // double-drop from starting two TUS uploads: `isUploading` is this
      // very prop, and it has not propagated back down from the parent yet
      // when Dropzone fires N events for one drop in the same tick -- every
      // one of them reads the same stale `false` here. What actually
      // prevents the second upload is the parent's synchronous
      // `if (this.local.isUploading) return` in onBunnyFileSelected, which
      // reads its own local flag rather than waiting on a prop round trip.
      if (this.isUploading) return;

      // vue2-dropzone's own acceptedFiles filter only rejects files chosen
      // through its click-to-browse dialog; the drag-and-drop path emits
      // this event for anything dropped on it. Without this check, dropping
      // e.g. a .pdf started the upload flow with a file the rest of the
      // pipeline (setVdoInfo's <video> element, then Bunny) cannot decode.
      if (ACCEPTED_EXTENSIONS.indexOf(extensionOf(file)) === -1) {
        this.$emit('rejected-file', file);
        return;
      }

      this.$emit('file-selected', file);
    },
    // vue2-dropzone wires a native click listener to its own root element
    // ($refs.dropzone.$el, the same node tabindex/role/aria-label are on)
    // that opens its hidden file input -- this dispatches that same click
    // so Enter/Space reach it too.
    triggerDropzoneClick() {
      if (this.$refs.dropzone && this.$refs.dropzone.$el) {
        this.$refs.dropzone.$el.click();
      }
    },
  },
  mounted() {
    // Not in data(): an interval id has no business being reactive.
    this.clock = setInterval(() => {
      this.now = Date.now();
    }, CLOCK_TICK_MS);
  },
  beforeDestroy() {
    clearInterval(this.clock);
  },
};

/**
 * @param {number} value
 * @return {string}
 */
function pad(value) {
  return String(value).padStart(2, '0');
}

/**
 * @param {File|null|undefined} file
 * @return {string} the lowercased extension including its leading dot, or
 *   '' when the file has none
 */
function extensionOf(file) {
  const name = (file && file.name) || '';
  const match = /\.[^.]+$/.exec(name);
  return match ? match[0].toLowerCase() : '';
}
</script>

<style lang="scss">
.bunny-upload-panel {
  .bunny-step-dot {
    width: 2rem;
    height: 2rem;
    border-radius: 9999px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.875rem;
    border: 2px solid #b8c2cc;
    color: #b8c2cc;

    &.is-done { border-color: #28c76f; background-color: #28c76f; color: #fff; }
    &.is-active { border-color: #7367f0; color: #7367f0; }
    &.is-error { border-color: #ea5455; background-color: #ea5455; color: #fff; }
  }

  .bunny-step-bar {
    flex: 1;
    height: 2px;
    margin: 0 0.5rem 1.25rem;
    background-color: #b8c2cc;

    &.is-done { background-color: #28c76f; }
  }

  .bunny-spinner {
    width: 1.25rem;
    height: 1.25rem;
    border: 2px solid rgba(115, 103, 240, 0.25);
    border-top-color: #7367f0;
    border-radius: 9999px;
    animation: bunny-spin 0.8s linear infinite;
  }

  // Vuexy/Tailwind's global reset removes the UA default focus ring from
  // most elements; the drop zone is now a tabindex="0" button-role element
  // (WCAG 2.1 AA 2.1.1), so it needs its own visible focus indicator.
  #bunny-dropzone:focus {
    outline: 2px solid #7367f0;
    outline-offset: 2px;
  }
}

@keyframes bunny-spin {
  to { transform: rotate(360deg); }
}
</style>
